"use client";

import { Fragment, useMemo, useState } from "react";

import { chordProseLabel } from "@/components/learn-piece/s0-copy";
import { OFFER_CLASS, OFFER_STYLE } from "@/components/learn-piece/shell/panel-metrics";
import { VIOLET_BRIGHT } from "@/components/ui/organisms/chord-practice/palette";
import { useChordLabel, useNoteLabel, useNoteNotation } from "@/hooks/use-notation";
import type { HarmonyContext } from "@/lib/harmony/hud";
import {
  borrowedLegend,
  chordInventory,
  degreeLegend,
  dominantLead,
  firstWindows,
  foldLine,
  inventoryLead,
  inventoryToggle,
  positionSpread,
  recurrenceCell,
  rungRail,
  sameChordsLead,
  sasScale,
  scaleCaption,
  sharedRootsLine,
  signatureLine,
  spreadLead,
  chordsLine,
  type SasChord,
  type SasInventory,
  type SasPosition,
  type SasScale,
  type SasWindow,
} from "@/lib/harmony/sas";
import { rungWords } from "@/lib/levels/words";
import type { NoteNotation } from "@/lib/music/notation";
import { positionName } from "@/lib/harmony/position";
import { formatAccidentals, windowToneName } from "@/lib/music/theory-helpers";
import { modeLabel, tonalityLabel, type Tonality } from "@/lib/music/tonality";
import {
  Prose,
  SasAction,
  SasColumns,
  SasLead,
  SasPage,
  SasRule,
} from "./sas-page";

/**
 * Le sas : le dernier écran immobile avant le décompte de quatre temps.
 *
 * Il ne juge rien, ne compte rien, n'enseigne pas. Il prépare les objets que le
 * joueur va voir bouger dix secondes plus tard, et il donne à ce niveau sa
 * version du morceau ouvert. C'est le seul écran du parcours harmonique qui
 * regarde le morceau **entier** et au repos ; l'écran de jeu, lui, ne montre
 * jamais que la fenêtre sous les mains.
 *
 * Il est le **second temps** du sas. L'entrée, juste avant, pose le morceau et
 * ouvre les deux parcours ; celui-ci ne parle plus que du niveau où l'on entre.
 * Les deux partagent leur décor et leurs gestes, qui vivent dans `sas-page` :
 * deux écrans qui se suivent à une seconde d'intervalle doivent être le même
 * objet.
 *
 * De la même famille que `SeamScreen` : une carte devant la
 * scène, `role="dialog"`, un bouton qui la quitte. Ce qui la distingue est
 * qu'elle porte quatre à cinq fois plus de texte, ce qui décide sa mise en page
 * et rien d'autre : la matière à gauche, la manière et le bouton à droite.
 *
 * ## L'inventaire est un tableau, ordonné par ce qui revient
 *
 * Il a été une liste groupée par qualité, et le groupement portait la leçon des
 * quatre écarts. Il lui manquait ce que le joueur demande en premier devant un
 * morceau qu'il ne connaît pas : *lequel de ces accords vais-je faire tout le
 * temps*. Un tableau trié par fréquence y répond d'un coup d'oeil, la barre
 * donnant l'écart entre le premier et le dernier sans qu'on compare des
 * nombres, et les quatre écarts restent dits par la phrase qui ouvre la colonne
 * de droite.
 *
 * Ce que le tableau ajoute et que la liste n'avait pas est la colonne des
 * **notes**. C'est elle qui rend la fiche de tonalité utile : *fa♯* revient sur
 * trois lignes et n'appartient à aucune gamme de la fiche, donc on voit, sans
 * qu'une phrase le dise, la seule chose que ce morceau fait et que sa tonalité
 * ne prévoit pas.
 *
 * ## Le retour sur un niveau déjà travaillé
 *
 * Le même écran, amputé de ses blocs d'enseignement. Rien du tout est exclu :
 * le décompte arrive quand même, et un écran qui disparaît un jour sur deux se
 * lit comme un bug. Le plein à chaque fois l'est aussi : rouvrir un niveau est
 * courant, et remettre cent trente mots devant un adulte qui les a lus est un
 * péage. Restent le surtitre, le titre, une ligne qui rouvre l'inventaire sur
 * place, et le bouton.
 *
 * Le bouton garde `Commencer` des deux côtés. Pas de « Reprendre » : selon où
 * est la tête de lecture on repart de la première unité ou de celle où l'on
 * était, et un libellé qui promettrait l'un ou l'autre serait faux une fois sur
 * deux.
 */

/**
 * Combien de fenêtres le roll du niveau 2 montre.
 *
 * Quatre, c'est-à-dire trois changements d'accord. Le dessin répond à une seule
 * question, est-ce que la main droite tient ses notes quand l'accord change, et
 * un seul changement y répondrait par un exemple pendant que dix en feraient un
 * tableau. Quatre colonnes laissent aussi à chaque barre une largeur où elle se
 * lit depuis le pupitre.
 *
 * Le début du niveau et non sa boucle : l'analyseur publie bien une boucle
 * harmonique, mais son propre commentaire la dit absente de la plupart des
 * morceaux, et une légende qui promettrait la boucle montrerait un début une
 * fois sur deux.
 */
const ROLL_WINDOWS = 4;

/**
 * La mesure de l'inventaire rouvert sur place.
 *
 * La version courte n'a pas de colonnes, donc rien ne bornerait le tableau : un
 * tableau étalé sur toute la pièce perd exactement ce que ses colonnes lui
 * donnent, l'oeil ne rapprochant plus le chiffrage de sa fréquence. La moitié
 * de la page, qui est la mesure d'une colonne de la version longue.
 */
const FOLDED_WIDTH_PX = 520;

/**
 * Le niveau dont le sujet est la position de l'accord, et le seul.
 *
 * Par indice canonique, comme `isSeamRung`, et pour la même
 * raison : le rang affiché saute les niveaux qu'un morceau n'a pas.
 */
const INVERSIONS_RUNG = 1;

/**
 * La longueur du rail de fréquence, le plus fréquent le remplissant en entier.
 *
 * Ce que la barre montre est un **écart entre les lignes** et non une part du
 * morceau : la première le remplit toujours, quel que soit le morceau, et les
 * autres se lisent contre elle. Une échelle rapportée au total aurait donné
 * quatorze traits courts sur un morceau qui répartit, et rien à comparer.
 */
const BAR_WIDTH_PX = 96;

/**
 * L'espace entre deux colonnes du tableau.
 *
 * Porté par les cellules et non par l'air qui reste : la mise en page
 * automatique n'écarte les colonnes que tant qu'il y a du mou, et sur la
 * tablette il n'y en a plus. `♯vii°` venait alors se coller à `Fa♯`, et la
 * ligne se lisait comme un seul mot.
 */
const GUTTER_PX = 18;

/**
 * Le corps des trois colonnes de texte du tableau.
 *
 * Degré, notes et fréquence se lisent ensemble, sur la même ligne et à la même
 * distance : trois mesures voisines mais différentes feraient trembler la ligne
 * sans rien dire de plus.
 */
const ROW_TEXT = "clamp(11px, 1.9cqh, 14px)";

export function SasScreen({
  harmony,
  title,
  composer,
  tonality,
  windows,
  chords,
  short,
  onStart,
}: {
  readonly harmony: HarmonyContext;
  readonly title: string | null;
  readonly composer: string | null;
  /**
   * La tonalité du morceau, ou `null` quand la détection n'a rien rendu.
   *
   * Elle ne s'affiche que si l'analyseur répond des degrés de ce morceau : le
   * drapeau ne dit pas seulement « pas de chiffres romains », il dit que la
   * tonalité n'est pas assez ferme pour les compter. Nommer « Si mineur » sur
   * l'écran dont le métier est de poser le cadre, quand c'est le cadre qui
   * n'est pas sûr, serait un mensonge discret.
   */
  readonly tonality: Tonality | null;
  /**
   * Ce que ce niveau donne à jouer, fenêtre par fenêtre.
   *
   * En prop et non dans `HarmonyContext`, qui porte l'harmonie du morceau et
   * les notes d'origine : ce qui va tomber sous les mains est dans le morceau
   * **chargé**, que seul l'appelant a. Il le découpe déjà pour son rail, et le
   * sas lit exactement le même découpage.
   */
  readonly windows: readonly SasWindow[];
  /** Combien de fenêtres d'accord ce niveau fait traverser. */
  readonly chords: number;
  /** Ce niveau a déjà été ouvert dans cette session : version courte. */
  readonly short: boolean;
  readonly onStart: () => void;
}) {
  const words = rungWords(harmony.rungId);
  const notation = useNoteNotation();
  const inventory = useMemo(() => chordInventory(harmony.chords), [harmony.chords]);
  const inversions = harmony.rungIndex === INVERSIONS_RUNG;
  const positions = useMemo(() => positionSpread(windows), [windows]);
  const roll = useMemo(() => firstWindows(windows, ROLL_WINDOWS), [windows]);

  /**
   * La gamme du morceau, ou `null` quand on n'en répond pas.
   *
   * Les deux conditions ensemble, et c'est la même que celle des degrés : sans
   * tonalité il n'y a pas de gamme, et sans degrés il y en a une dont on ne
   * répond pas. Elle emporte la fiche, la colonne des degrés et l'encre des
   * notes empruntées d'un seul geste — trois objets qui disent tous « dans
   * cette tonalité-là », et qui doivent donc apparaître et disparaître
   * ensemble.
   */
  const scale = useMemo(
    () =>
      tonality === null || !harmony.degreesTrusted ? null : sasScale(tonality, inventory),
    [tonality, harmony.degreesTrusted, inventory],
  );

  // Le morceau ouvert, et son compositeur quand il est connu. L'entrée l'a mis
  // en titraille ; ici il redescend en surtitre, la titraille revenant au
  // niveau.
  const overline = composer === null || composer === "" ? title : `${title} · ${composer}`;

  /**
   * Le bloc central, ou `null` quand ce niveau n'en a pas dans ce morceau.
   *
   * Le niveau 2 le perd sur un morceau où aucun accord ne change de position :
   * sans cette condition la vignette « maintenant » serait identique à
   * « avant », ce qui démontrerait l'inverse. La colonne disparaît alors avec
   * lui, et la manière prend l'écran seule — une colonne vide en face d'un
   * texte se lit comme un chargement qui n'a pas fini.
   */
  const matter = inversions ? (
    roll.length === 0 ? null : <Roll windows={roll} scale={scale} keyTonic={tonality?.tonic ?? null} />
  ) : (
    <Inventory inventory={inventory} scale={scale} />
  );

  return (
    <SasPage
      label={words.label}
      overline={overline}
      aside={rungRail(rungRank(harmony), chords)}
      title={words.label}
      lead={
        short ? null : inversions ? (
          <SasLead
            first={sameChordsLead(inventory)}
            second="Ce qui change, c'est l'ordre des trois notes de la main droite."
          />
        ) : (
          <SasLead
            first={inventoryLead(inventory)}
            second={
              inventory.dominant === null
                ? null
                : dominantLead(
                    chordProseLabel({ chord: inventory.dominant[0] }, notation),
                    chordProseLabel({ chord: inventory.dominant[1] }, notation),
                    inventory.dominant[0].count + inventory.dominant[1].count,
                  )
            }
          />
        )
      }
      /*
        Chaque niveau pose sa fiche à côté du titre, et c'est le même créneau
        parce que c'est la même question : dans quel cadre ce qu'on va jouer se
        lit-il. Au niveau 1 c'est la tonalité du morceau, au niveau 2 les trois
        positions et ce que ce morceau-ci en fait.

        La version courte n'en a pas : elle garde son tableau replié, et une
        fiche ouverte au-dessus d'un repli rouvrirait par la bande ce que ce
        repli vient de fermer.
      */
      figure={
        short ? null : inversions ? (
          <PositionsCard positions={positions} />
        ) : scale === null ? null : (
          <KeyCard scale={scale} />
        )
      }
    >
      {short ? (
        <>
          <div style={{ marginTop: 16 }}>
            <FoldedInventory inventory={inventory} scale={scale} />
          </div>

          <div style={{ marginTop: 20 }}>
            <Start onStart={onStart} />
          </div>
        </>
      ) : (
        <>
          <SasRule />

          <SasColumns
            left={matter}
            right={
              <>
                {inversions ? (
                  <Inversions />
                ) : (
                  <Reading inventory={inventory} scale={scale} notation={notation} />
                )}

                <Procedure chords={chords} top={10} inversions={inversions} />

                <div style={{ marginTop: "auto", paddingTop: 8 }}>
                  <Start onStart={onStart} />
                </div>
              </>
            }
          />
        </>
      )}
    </SasPage>
  );
}

/**
 * Le rang de ce niveau dans l'échelle **de ce morceau**.
 *
 * La position dans l'échelle et non l'indice canonique, exactement comme
 * `RungMap` la renumérote : un morceau qui n'a rien à apprendre au niveau
 * canonique 2 ouvre le suivant au rang trois, et afficher quatre y ferait
 * réapparaître l'absent sous forme de trou. Le repli sur l'indice couvre
 * l'échelle vide, où il n'y a rien à compter.
 */
function rungRank(harmony: HarmonyContext): number {
  const at = harmony.ladder.findIndex((rung) => rung.index === harmony.rungIndex);

  return at < 0 ? harmony.rungIndex + 1 : at + 1;
}

// ── La fiche : la tonalité, son armure et sa gamme ─────────────

/**
 * La tonalité du morceau, posée à côté du titre.
 *
 * Elle ne s'adresse pas au joueur qui sait déjà lire : *sol mineur* ne dit rien
 * à qui n'a jamais ouvert de partition. Ce qui parle est en dessous — deux
 * bémols, lesquels, et les sept notes dans l'ordre avec les accords que le
 * morceau bâtit dessus — et c'est aussi ce qui donne son sens à la colonne des
 * notes du tableau : ce qui n'est pas dans cette bande-là est emprunté.
 *
 * Une fiche et non une frise : `ScaleFrieze` enseigne le patron d'intervalles,
 * tons et demi-tons compris, et c'est un cours de deux minutes. Ici la gamme est
 * un **cadre de lecture** posé au-dessus d'un tableau qu'on va parcourir, et
 * elle a trois lignes pour le poser.
 */
function KeyCard({ scale }: { readonly scale: SasScale }) {
  const toNoteLabel = useNoteLabel();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "clamp(7px, 1.6cqh, 12px)",
        padding: "clamp(9px, 2cqh, 14px) 17px",
        border: "1px solid rgba(255,255,255,0.1)",
        background: "rgba(255,255,255,0.014)",
      }}
    >
      <div className="flex items-end justify-between" style={{ gap: 18 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <CardLabel>Tonalité</CardLabel>

          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 200,
              fontSize: "clamp(21px, 4cqh, 32px)",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: "rgba(255,255,255,0.95)",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: VIOLET_BRIGHT }}>
              {toNoteLabel(scale.tonality.tonic)}
            </span>{" "}
            {modeLabel(scale.tonality.mode)}
          </div>
        </div>

        {scale.signature !== null && (
          <div
            className="font-mono"
            style={{
              fontSize: "clamp(10px, 1.6cqh, 12.5px)",
              lineHeight: 1.7,
              letterSpacing: "0.1em",
              textAlign: "right",
              color: "var(--color-text-secondary)",
            }}
          >
            {signatureLine(scale.signature)}
            {scale.signature.notes.length > 0 && (
              <>
                <br />
                {scale.signature.notes.map((note) => toNoteLabel(note)).join("  ")}
              </>
            )}
          </div>
        )}
      </div>

      <div aria-hidden style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <CardLabel quiet>{scaleCaption(scale.borrowed.length)}</CardLabel>

        {/*
          Une seule ligne tant que les emprunts y tiennent, et le groupe
          emprunté passe dessous entier quand ils n'y tiennent plus.

          Ni rétrécir ni couper : les serrer jusqu'à ce que ça rentre rendrait
          illisible ce qu'on est venu lire ici, à un bras du pupitre, et une
          bande qui défile cacherait le dernier emprunt derrière un geste que
          rien n'annonce. Le nombre d'emprunts est ce qui varie d'un morceau à
          l'autre — deux sur un mineur ordinaire, cinq sur un morceau qui module
          — donc c'est lui qui décide du pli, et la gamme reste d'un tenant
          quoi qu'il arrive.
        */}
        <div className="flex flex-wrap items-end" style={{ gap: "13px 11px" }}>
          <div className="flex items-end" style={{ gap: 11 }}>
            {scale.degrees.map((entry) => (
              <ScaleStep
                key={entry.pitchClass}
                note={toNoteLabel(entry.name)}
                degree={String(entry.degree)}
                numerals={entry.romanNumerals}
              />
            ))}
          </div>

          {scale.borrowed.length > 0 && (
            <div className="flex items-end" style={{ gap: 11 }}>
              <span
                aria-hidden
                className="shrink-0 self-center"
                style={{
                  width: 1,
                  height: 26,
                  marginRight: 5,
                  background: "rgba(255,255,255,0.18)",
                }}
              />

              {scale.borrowed.map((entry) => (
                <ScaleStep
                  key={entry.pitchClass}
                  note={toNoteLabel(entry.name)}
                  degree={entry.degree}
                  numerals={entry.romanNumerals}
                  borrowed
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Un intertitre de la fiche. */
function CardLabel({
  children,
  quiet = false,
}: {
  readonly children: React.ReactNode;
  /** Les deux ne sont pas du même rang : `Tonalité` nomme la fiche, l'autre une bande. */
  readonly quiet?: boolean;
}) {
  return (
    <div
      className="font-mono uppercase"
      style={{
        fontSize: "clamp(9.5px, 1.5cqh, 11.5px)",
        lineHeight: "13px",
        letterSpacing: quiet ? "0.2em" : "0.24em",
        color: quiet ? "var(--color-text-secondary)" : VIOLET_BRIGHT,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Une colonne de la bande : la note, son rang, et ce que le morceau bâtit
 * dessus.
 *
 * Le point tient la place du chiffrage quand ce morceau ne pose aucun accord
 * sur ce degré-là. Une cellule vide ferait remonter les deux lignes du dessous
 * d'une colonne à l'autre et la bande cesserait de se lire en lignes ; le point
 * dit la même chose et ne bouge rien.
 */
function ScaleStep({
  note,
  degree,
  numerals,
  borrowed = false,
}: {
  readonly note: string;
  readonly degree: string;
  readonly numerals: readonly string[];
  /** Hors gamme : la colonne prend l'encre du tableau pour les mêmes notes. */
  readonly borrowed?: boolean;
}) {
  return (
    <div
      className="flex shrink-0 flex-col items-center"
      style={{ gap: 3 }}
    >
      <span
        style={{
          fontSize: "clamp(14px, 2.35cqh, 20px)",
          lineHeight: 1,
          color: borrowed ? VIOLET_BRIGHT : "rgba(255,255,255,0.95)",
        }}
      >
        {note}
      </span>

      <span
        className="font-mono"
        style={{
          fontSize: "clamp(9.5px, 1.55cqh, 12px)",
          lineHeight: 1,
          color: borrowed ? "rgba(196,181,253,0.7)" : "var(--color-text-secondary)",
        }}
      >
        {formatAccidentals(degree)}
      </span>

      <span
        className="font-mono whitespace-nowrap"
        style={{
          fontSize: "clamp(9.5px, 1.55cqh, 12px)",
          lineHeight: 1,
          color: numerals.length === 0 ? "rgba(255,255,255,0.28)" : VIOLET_BRIGHT,
        }}
      >
        {numerals.length === 0 ? "·" : formatAccidentals(numerals.join(" "))}
      </span>
    </div>
  );
}

// ── Bloc 4, niveau 1 : l'inventaire ────────────────────────────

/**
 * Les accords du morceau, du plus rencontré au moins rencontré.
 *
 * Quatre colonnes qui répondent chacune à une question qu'on se pose devant un
 * morceau qu'on n'a pas encore joué : comment il s'appelle, où il tombe dans la
 * tonalité, quelles touches il demande, et combien de fois il va revenir. La
 * barre double la dernière parce qu'un écart entre cinquante-cinq et deux ne se
 * lit pas sur deux nombres alignés.
 *
 * La colonne des degrés et l'encre des notes empruntées tombent ensemble quand
 * il n'y a pas de gamme : les deux disent « dans cette tonalité-là », et l'une
 * sans l'autre serait une couleur qu'aucune légende n'explique.
 */
function Inventory({
  inventory,
  scale,
}: {
  readonly inventory: SasInventory;
  /** La gamme, ou `null` quand on ne répond pas des degrés de ce morceau. */
  readonly scale: SasScale | null;
}) {
  const legend = scale === null ? null : borrowedLegend(scale.borrowed.length);
  // Le sommet de l'échelle des barres. Le plus fréquent tient la largeur
  // entière : ce que la barre montre est un écart entre les lignes, pas une
  // part du morceau.
  const peak = Math.max(...inventory.rows.map((entry) => entry.count), 1);

  return (
    <>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <Column>Accord</Column>
            {scale !== null && <Column>Degré</Column>}
            <Column>Notes</Column>
            <Column end>Fréquence</Column>
            <Column />
          </tr>
        </thead>

        <tbody>
          {inventory.rows.map((entry, at) => (
            <Line
              key={entry.symbol}
              entry={entry}
              scale={scale}
              peak={peak}
              last={at === inventory.rows.length - 1}
            />
          ))}
        </tbody>
      </table>

      {(inventory.fold !== null || legend !== null) && (
        <div
          className="flex justify-between"
          style={{
            gap: 24,
            marginTop: 9,
            fontSize: "clamp(12px, 1.95cqh, 15px)",
            lineHeight: 1.5,
          }}
        >
          <span style={{ color: "var(--color-text-secondary)" }}>
            {inventory.fold === null ? "" : foldLine(inventory.fold)}
          </span>

          {legend !== null && (
            <span className="shrink-0" style={{ color: VIOLET_BRIGHT }}>
              {legend}
            </span>
          )}
        </div>
      )}
    </>
  );
}

/** Un en-tête de colonne du tableau. */
function Column({
  children,
  end = false,
}: {
  readonly children?: React.ReactNode;
  /** Alignée à droite, comme la colonne des nombres qu'elle coiffe. */
  readonly end?: boolean;
}) {
  return (
    <th
      className="font-mono uppercase"
      style={{
        padding: end ? `0 0 9px ${GUTTER_PX}px` : `0 ${GUTTER_PX}px 9px 0`,
        textAlign: end ? "right" : "left",
        fontSize: "clamp(9.5px, 1.55cqh, 11.5px)",
        fontWeight: 400,
        letterSpacing: "0.2em",
        color: "var(--color-text-secondary)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {children}
    </th>
  );
}

/** Une ligne du tableau : un accord, et les quatre choses qu'on en dit. */
function Line({
  entry,
  scale,
  peak,
  last,
}: {
  readonly entry: SasChord;
  readonly scale: SasScale | null;
  readonly peak: number;
  readonly last: boolean;
}) {
  const toChordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();
  // La cellule vide marque d'elle-même les accords rares, et un accord sans
  // chiffrage garde sa cellule vide : pas de tiret, pas de point
  // d'interrogation, pas de degré deviné.
  const recurrence = recurrenceCell(entry.count);
  // Le rembourrage suit la hauteur du cadre comme le corps des lettres : huit
  // lignes d'accord multiplient par huit le moindre pixel donné à une ligne,
  // donc c'est ici que le tableau tient ou déborde.
  const pad = "clamp(3px, 0.8cqh, 8px)";
  const cell: React.CSSProperties = {
    padding: `${pad} ${GUTTER_PX}px ${pad} 0`,
    // L'interligne est cadrée et non laissée à la fonte. Sans elle, la hauteur
    // d'une ligne sortait de la boîte de texte du chiffrage, la plus grande des
    // cinq cellules, ce qui donnait cinq pixels de plus par ligne et quarante
    // sur le tableau : le prix d'un réglage que personne n'avait choisi.
    lineHeight: 1.2,
    verticalAlign: "middle",
    borderBottom: last ? "none" : "1px solid rgba(255,255,255,0.06)",
  };

  return (
    <tr>
      {/*
        Le chiffrage garde la chasse fixe du produit, qui l'écrit ainsi partout
        ailleurs, et prend la taille que le dessin lui donne : c'est l'ancre de
        la ligne, le seul mot qu'on cherche en parcourant la colonne.
      */}
      <td
        className="font-mono whitespace-nowrap"
        style={{ ...cell, fontSize: "clamp(15px, 2.8cqh, 23px)", color: VIOLET_BRIGHT }}
      >
        {toChordLabel(entry.symbol)}
      </td>

      {scale !== null && (
        <td
          className="font-mono"
          style={{ ...cell, fontSize: ROW_TEXT, color: "rgba(255,255,255,0.78)" }}
        >
          {formatAccidentals(entry.romanNumeral)}
        </td>
      )}

      <td
        className="font-mono"
        style={{
          ...cell,
          fontSize: ROW_TEXT,
          letterSpacing: "0.08em",
          whiteSpace: "nowrap",
        }}
      >
        {entry.notes.map((tone, at) => (
          <span
            key={tone.pitchClass}
            style={{
              color:
                scale !== null && !scale.pitchClasses.has(tone.pitchClass)
                  ? VIOLET_BRIGHT
                  : "var(--color-text-secondary)",
            }}
          >
            {at > 0 && " "}
            {toNoteLabel(tone.name)}
          </span>
        ))}
      </td>

      <td
        className="font-mono tabular-nums"
        style={{
          ...cell,
          fontSize: ROW_TEXT,
          padding: `${pad} 0 ${pad} ${GUTTER_PX}px`,
          textAlign: "right",
          whiteSpace: "nowrap",
          color: "rgba(255,255,255,0.92)",
        }}
      >
        {recurrence ?? ""}
      </td>

      {/*
        La mesure de la barre est portée par le rail lui-même et non par sa
        cellule. Une largeur posée sur un `<td>` n'est qu'un vœu : la mise en
        page automatique sert d'abord les colonnes qui ont du contenu, et un
        rail vide se faisait écraser à vingt pixels, où l'écart entre
        cinquante-cinq et deux ne se voyait plus.
      */}
      <td style={{ ...cell, padding: `${pad} 0 ${pad} ${GUTTER_PX}px` }}>
        <div style={{ width: BAR_WIDTH_PX, height: 2, background: "rgba(255,255,255,0.08)" }}>
          <div
            style={{
              height: 2,
              width: `${Math.round((entry.count / peak) * 100)}%`,
              background: "rgba(194,178,249,0.7)",
            }}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * La version courte : la ligne qui rouvre l'inventaire sur place.
 *
 * Repliée et non retirée : ce que le sas garde et qui n'est nulle part ailleurs
 * est justement cet inventaire au repos, et quelqu'un qui rouvre un niveau
 * trois jours plus tard a le droit de le redemander sans repayer les cent
 * trente mots qui l'entourent.
 */
function FoldedInventory({
  inventory,
  scale,
}: {
  readonly inventory: SasInventory;
  readonly scale: SasScale | null;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={OFFER_CLASS}
        style={OFFER_STYLE}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {inventoryToggle(inventory.chords)}
      </button>

      {open && (
        <div style={{ marginTop: 12, maxWidth: FOLDED_WIDTH_PX }}>
          <Inventory inventory={inventory} scale={scale} />
        </div>
      )}
    </>
  );
}

/**
 * Ce qu'il faut savoir pour lire le tableau d'à côté.
 *
 * Trois phrases, et chacune répond à une colonne : ce que les symboles ont en
 * commun, ce que le chiffre romain dit, et pourquoi deux lignes commencent par
 * la même note. Elles vivent à droite et non sous le tableau parce qu'on les lit
 * une fois, tandis que le tableau se reparcourt.
 */
function Reading({
  inventory,
  scale,
  notation,
}: {
  readonly inventory: SasInventory;
  readonly scale: SasScale | null;
  readonly notation: NoteNotation;
}) {
  const shared = sharedRootsLine(
    inventory.sharedRoots.map((family) =>
      family.map((entry) => chordProseLabel({ chord: entry }, notation)),
    ),
  );

  return (
    <>
      <Prose>{spreadLead(inventory.groups.length)}</Prose>

      {scale !== null && (
        <Prose top={7}>{degreeLegend(tonalityLabel(scale.tonality, notation))}</Prose>
      )}

      {shared !== null && <Prose top={7}>{shared}</Prose>}

      <SasRule />
    </>
  );
}

// ── Bloc 4, niveau 2 : les positions, et ce qu'elles donnent ───

/**
 * Les trois positions, et ce que ce morceau-ci en fait.
 *
 * La fiche du niveau 2, dans le créneau où le niveau 1 pose sa tonalité. Elle
 * porte la connaissance entière du barreau : trois positions, leurs noms, et le
 * fait qui les nomme, la note du bas. Le degré souligné dans chaque ligne est
 * ce fait, montré au lieu d'être dit.
 *
 * La colonne des fréquences est ce qui l'empêche d'être une affiche de théorie :
 * elle dit que ce morceau-ci prend le premier renversement presque aussi souvent
 * que l'état fondamental, et c'est une raison de le travailler. C'est aussi le
 * même geste qu'au niveau 1, où la même colonne classe les accords.
 *
 * Les trois lignes sortent toujours, y compris à zéro. Elles se lisent ensemble,
 * et une fiche à deux lignes sur un morceau qui ne croise pas le deuxième
 * renversement laisserait croire qu'il n'en existe que deux.
 */
function PositionsCard({ positions }: { readonly positions: readonly SasPosition[] }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "clamp(7px, 1.6cqh, 12px)",
        padding: "clamp(9px, 2cqh, 14px) 17px",
        border: "1px solid rgba(255,255,255,0.1)",
        background: "rgba(255,255,255,0.014)",
      }}
    >
      <div className="flex items-start justify-between" style={{ gap: 18 }}>
        <CardLabel>Les trois positions</CardLabel>

        <div
          className="font-mono"
          style={{
            fontSize: "clamp(10px, 1.6cqh, 12.5px)",
            lineHeight: 1.7,
            letterSpacing: "0.1em",
            textAlign: "right",
            color: "var(--color-text-secondary)",
          }}
        >
          la note du bas
          <br />
          nomme la position
        </div>
      </div>

      <div aria-hidden style={{ height: 1, background: "rgba(255,255,255,0.1)" }} />

      <div>
        {positions.map((position, at) => (
          <PositionRow
            key={position.inversion}
            position={position}
            last={at === positions.length - 1}
          />
        ))}
      </div>

      <div
        className="font-mono"
        style={{
          fontSize: "clamp(9.5px, 1.5cqh, 11.5px)",
          letterSpacing: "0.08em",
          color: "rgba(255,255,255,0.32)",
        }}
      >
        1 fondamentale · 3 tierce · 5 quinte
      </div>
    </div>
  );
}

/** Une position : son nom, l'ordre de sa pile, et ce que ce morceau en fait. */
function PositionRow({
  position,
  last,
}: {
  readonly position: SasPosition;
  readonly last: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        alignItems: "baseline",
        columnGap: "clamp(12px, 1.4cqw, 24px)",
        padding: last
          ? "clamp(4px, 1.1cqh, 8px) 0 0"
          : "clamp(4px, 1.1cqh, 8px) 0",
        borderBottom: last ? "none" : "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <span
        style={{
          fontSize: "clamp(14px, 2.2cqh, 19px)",
          lineHeight: 1.15,
          color: "rgba(255,255,255,0.95)",
        }}
      >
        {positionName(position.inversion)}
      </span>

      {/*
        La pile, la note du bas en tête. Le premier degré est souligné parce
        que c'est lui qui nomme la ligne : la règle tient dans le dessin, et la
        phrase à côté n'a plus qu'à la pointer.
      */}
      <span
        className="font-mono whitespace-nowrap"
        style={{
          fontSize: "clamp(10.5px, 1.55cqh, 13px)",
          letterSpacing: "0.34em",
          color: "var(--color-text-secondary)",
        }}
      >
        {position.degrees.map((degree, at) => (
          <span
            key={degree}
            style={
              at === 0
                ? {
                    color: "rgba(255,255,255,0.95)",
                    borderBottom: "1px solid rgba(255,255,255,0.18)",
                    paddingBottom: 3,
                  }
                : undefined
            }
          >
            {degree}
            {at < position.degrees.length - 1 ? " " : ""}
          </span>
        ))}
      </span>

      <span
        className="font-mono tabular-nums whitespace-nowrap"
        style={{
          fontSize: "clamp(10px, 1.45cqh, 12.5px)",
          textAlign: "right",
          color: VIOLET_BRIGHT,
        }}
      >
        {position.count} fois
      </span>
    </div>
  );
}

/**
 * Le début du niveau, en rouleau.
 *
 * Le seul objet de tout le parcours harmonique qui montre les **notes du
 * niveau** au repos. Il remplace deux vignettes de clavier qui comparaient un
 * avant fabriqué à un après fabriqué : elles répondaient à « quelle note est en
 * bas », ce que la fiche dit maintenant en trois lignes, et elles fabriquaient
 * leurs hauteurs faute de pouvoir lire celles du morceau. Ici les hauteurs sont
 * les vraies, et ce qu'on lit est ce qu'on va jouer.
 *
 * Ce que le dessin donne et qu'aucune phrase ne donne : les barres alignées.
 * Deux accords qui se suivent partagent des notes, et au niveau 2 la main droite
 * les garde en place au lieu de sauter. C'est la leçon du barreau, et elle se
 * voit d'un coup d'œil sans qu'on ait à croire personne.
 *
 * La main gauche reste en lettres et non en barres. Elle joue la fondamentale à
 * l'octave la plus proche de son point d'ancrage, donc son mouvement est de
 * quelques demi-tons et un axe de hauteurs le rendrait à la fois minuscule et
 * trompeur : ce qui compte d'elle est **quel accord elle nomme**, pas où elle
 * se pose. Elle porte donc son nom, dans la couleur de main du produit.
 */
function Roll({
  windows,
  scale,
  keyTonic,
}: {
  readonly windows: readonly SasWindow[];
  /** La gamme, pour marquer les hauteurs empruntées comme au niveau 1. */
  readonly scale: SasScale | null;
  /** L'orthographe de la tonique, pour les notes que l'accord ne nomme pas. */
  readonly keyTonic: string | null;
}) {
  const toChordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();
  const rows = pitchRows(windows, keyTonic);
  const columns = `auto repeat(${windows.length}, minmax(0, 1fr))`;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: columns, alignItems: "center" }}>
        <HandLabel>Main droite</HandLabel>

        {windows.map((window, at) => (
          <div
            key={`sym-${at}`}
            className="whitespace-nowrap text-center"
            style={{
              paddingBottom: "clamp(5px, 1.1cqh, 12px)",
              fontSize: "clamp(15px, 2.8cqh, 23px)",
              lineHeight: 1,
              color: VIOLET_BRIGHT,
            }}
          >
            {toChordLabel(window.symbol)}
          </div>
        ))}

        {rows.map((row) => (
          <Fragment key={row.midi}>
            <div
              className="font-mono whitespace-nowrap text-right"
              style={{
                paddingRight: "clamp(8px, 1cqw, 16px)",
                fontSize: "clamp(9.5px, 1.5cqh, 12px)",
                color:
                  scale !== null && !scale.pitchClasses.has(row.pitchClass)
                    ? VIOLET_BRIGHT
                    : "var(--color-text-secondary)",
              }}
            >
              {toNoteLabel(row.name)}
            </div>

            {windows.map((window, at) => (
              <div
                key={`${row.midi}-${at}`}
                className="relative flex items-center"
                style={{ height: "clamp(13px, 2.5cqh, 30px)" }}
              >
                {/* Le filet de la voie, qui rend l'alignement lisible même là
                    où la note ne sonne pas. */}
                <span
                  aria-hidden
                  className="absolute left-0 right-0"
                  style={{ top: "50%", height: 1, background: "rgba(255,255,255,0.06)" }}
                />

                {window.right.includes(row.midi) && (
                  <span
                    className="relative block w-full"
                    style={{
                      height: "clamp(6px, 1.2cqh, 12px)",
                      margin: "0 2px",
                      background: "var(--color-hand-right)",
                    }}
                  />
                )}
              </div>
            ))}
          </Fragment>
        ))}

        <div
          aria-hidden
          style={{
            gridColumn: "1 / -1",
            height: 1,
            margin: "clamp(6px, 1.2cqh, 13px) 0",
            background: "rgba(255,255,255,0.1)",
          }}
        />

        <HandLabel bottom>Main gauche</HandLabel>

        {windows.map((window, at) => (
          <div
            key={`bass-${at}`}
            className="whitespace-nowrap text-center"
            style={{
              fontSize: "clamp(13px, 2.3cqh, 20px)",
              lineHeight: 1,
              color: "var(--color-hand-left)",
            }}
          >
            {bassName(window, toNoteLabel, keyTonic)}
          </div>
        ))}
      </div>

      <div
        className="flex justify-between"
        style={{
          gap: 24,
          marginTop: 9,
          fontSize: "clamp(12px, 1.95cqh, 15px)",
          lineHeight: 1.5,
        }}
      >
        <span style={{ color: "var(--color-text-secondary)" }}>
          Les premiers accords de ce niveau.
        </span>

        <span className="shrink-0" style={{ color: VIOLET_BRIGHT }}>
          barres alignées : la note ne bouge pas
        </span>
      </div>
    </>
  );
}

/** Laquelle des deux mains la voie dessine. */
function HandLabel({
  children,
  bottom = false,
}: {
  readonly children: React.ReactNode;
  /** La voie du bas, dont l'étiquette n'a rien à surplomber. */
  readonly bottom?: boolean;
}) {
  return (
    <div
      className="font-mono whitespace-nowrap text-right"
      style={{
        paddingRight: "clamp(8px, 1cqw, 16px)",
        paddingBottom: bottom ? 0 : "clamp(5px, 1.1cqh, 12px)",
        fontSize: "clamp(9.5px, 1.5cqh, 11.5px)",
        color: bottom ? "var(--color-hand-left)" : "var(--color-hand-right)",
      }}
    >
      {children}
    </div>
  );
}

/** Une voie du rouleau : une hauteur, son nom, et sa classe. */
type PitchRow = {
  readonly midi: number;
  readonly pitchClass: number;
  readonly name: string;
};

/**
 * Les hauteurs de la main droite, de la plus aiguë à la plus grave.
 *
 * Une voie par **hauteur** et non par classe : deux fenêtres qui jouent le même
 * sol à deux octaves d'écart sont deux endroits du clavier, et les mettre sur la
 * même voie ferait dire au dessin que la note ne bouge pas alors qu'elle a
 * changé d'octave, ce qui est exactement ce qu'il est là pour montrer.
 *
 * Chaque hauteur est épelée par la **première fenêtre qui la joue**, sur le
 * degré qu'elle y occupe : c'est la règle du tableau du niveau 1, et la seule
 * qui écrive *si bémol* et non *la dièse* dans un sol mineur.
 */
function pitchRows(
  windows: readonly SasWindow[],
  /** L'orthographe de la tonique, pour les notes que l'accord ne nomme pas. */
  keyTonic: string | null,
): readonly PitchRow[] {
  const seen = new Map<number, PitchRow>();

  for (const window of windows) {
    for (const midi of window.right) {
      if (seen.has(midi)) {
        continue;
      }

      const pitchClass = ((midi % 12) + 12) % 12;

      seen.set(midi, {
        midi,
        pitchClass,
        name: windowToneName({
          spelled: window.spelling?.get(midi) ?? null,
          pitchClass,
          rootSpelling: window.root,
          rootPitchClass: window.rootPitchClass,
          chordPitchClasses: window.chordPitchClasses,
          keyTonic,
        }),
      });
    }
  }

  return [...seen.values()].sort((a, b) => b.midi - a.midi);
}

/**
 * Le nom de ce que la main gauche pose, ou une cellule vide.
 *
 * Sa note la plus grave, et non la fondamentale de l'accord : aux niveaux
 * fabriqués les deux coïncident, mais c'est ce que la main joue qui est écrit
 * ici, pas ce qu'on attend d'elle. Le jour où un niveau lui donne autre chose,
 * le dessin le dira au lieu de mentir.
 */
function bassName(
  window: SasWindow,
  toNoteLabel: (name: string) => string,
  keyTonic: string | null,
): string {
  const lowest = Math.min(...window.left);

  if (window.left.length === 0 || !Number.isFinite(lowest)) {
    return "";
  }

  const pitchClass = ((lowest % 12) + 12) % 12;

  return toNoteLabel(
    windowToneName({
      spelled: window.spelling?.get(lowest) ?? null,
      pitchClass,
      rootSpelling: window.root,
      rootPitchClass: window.rootPitchClass,
      chordPitchClasses: window.chordPitchClasses,
      keyTonic,
    }),
  );
}

/**
 * Ce qu'il faut savoir pour lire le rouleau d'à côté.
 *
 * Trois phrases : ce qu'est un renversement, quelle main le porte, et pourquoi
 * l'autre ne bouge pas de rôle.
 *
 * La deuxième est la plus importante des deux écrans du sas. Elle dit la vérité
 * technique, elle désamorce le contresens de l'adulte qui a déjà entendu parler
 * de renversements, et elle explique un fait observable que rien d'autre
 * n'explique : le rail continue d'afficher `Gm` et jamais `Gm/Si♭`.
 *
 * La troisième énonce une **règle** du barreau et non un fait de ce morceau : le
 * niveau prend, à chaque accord, la position qui tombe le plus près de la main.
 * Compter combien de notes tiennent sur tel changement aurait été vrai d'une
 * boucle et faux de la suivante ; la règle, elle, vaut partout, et le dessin la
 * vérifie sous les yeux du joueur.
 */
function Inversions() {
  return (
    <>
      <Prose>
        {"Un renversement, c'est le même accord dans un autre ordre. D'une position à la " +
          "suivante, la note du bas monte d'une octave et les deux autres restent où elles sont."}
      </Prose>

      <Prose top={7}>
        {"Le renversement est dans la main droite. La gauche, elle, tient toujours la " +
          "fondamentale, et c'est pour ça que le nom de l'accord ne change pas."}
      </Prose>

      <Prose top={7}>
        {"Du coup la main droite cesse de sauter d'un accord à l'autre : elle prend à chaque " +
          "fois la position qui tombe le plus près d'elle, et garde souvent une ou deux notes " +
          "en place."}
      </Prose>

      <SasRule />
    </>
  );
}

// ── Blocs 5 et 6 : la manière, puis on y va ────────────────────

/**
 * Comment ça va se passer.
 *
 * Ces phrases sont ce que le sas garde et qu'aucun autre écran ne porte.
 * « Rien ne sonne tant que tu n'as pas posé toutes les touches » est le
 * mécanisme central du parcours, et il est contre-intuitif :
 * tout le reste du produit fait partir la musique tout seul. Sans cette phrase,
 * les dix premières secondes sont un écran muet qu'on croit cassé.
 * « Tu peux relâcher, il va jusqu'au bout » est l'autre moitié, sans quoi on
 * reste crispé sur les touches pour rien.
 *
 * ## Une seule règle, deux longueurs
 *
 * Au niveau 2 elle tient en une phrase, et ce n'est pas une seconde
 * formulation : c'est la même règle rappelée à quelqu'un qui vient de la vivre
 * vingt-trois fois. La détailler à nouveau serait un péage, la retirer
 * laisserait croire qu'elle a changé. Ce qui ne se raccourcit pas est la partie
 * contre-intuitive, l'écran qui attend.
 */
function Procedure({
  chords,
  top,
  inversions,
}: {
  readonly chords: number;
  readonly top: number;
  /** Le joueur sort du niveau 1 : la consigne se rappelle au lieu de s'expliquer. */
  readonly inversions: boolean;
}) {
  return (
    <>
      {inversions ? (
        <Prose top={top}>
          {"L'écran attend toujours toutes les touches, des deux mains. Puis le morceau " +
            "repart, ralenti."}
        </Prose>
      ) : (
        <>
          <Prose top={top}>
            {"Sur chaque accord, l'écran attend. Rien ne sonne tant que tu n'as pas posé " +
              "toutes les touches, des deux mains : c'est toi qui déclenches la musique, donc " +
              "tu ne peux pas être en retard sur elle."}
          </Prose>

          <Prose top={7}>
            {"Dès que l'accord est plaqué, tu entends le morceau tel qu'il est écrit, ralenti. " +
              "Tu peux relâcher, il va jusqu'au bout."}
          </Prose>
        </>
      )}

      <Prose top={7}>{chordsLine(chords)}</Prose>
    </>
  );
}

/**
 * Le geste du sas : on y va.
 *
 * Le même geste que l'entrée juste avant, au pixel près, et que la couture :
 * trois écrans de la même famille ne se distinguent pas par la forme de leur
 * sortie.
 *
 * Sans légende. Elle annonçait le décompte, « Quatre temps, puis le premier
 * accord », et c'était dire à l'avance ce que les quatre secondes suivantes
 * disent d'elles-mêmes, sous un bouton dont le mot suffit. Une ligne de plus
 * sous le dernier objet de l'écran est aussi la première que le cadre du studio
 * pousse hors de vue.
 *
 * `Commencer` des deux côtés, version longue comme version courte. Pas de
 * « Reprendre » : selon où est la tête de lecture on repart de la première
 * unité ou de celle où l'on était, et un libellé qui promettrait l'un ou
 * l'autre serait faux une fois sur deux.
 */
function Start({ onStart }: { readonly onStart: () => void }) {
  return (
    <SasAction label="Commencer" hint={null} onClick={onStart} />
  );
}
